import { IButtonStyles, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontWeights, Sizes } from '../../themes/styles';

export interface IKeyValuePairEditorClassnames {
  root: string;
  header: string;
  keyColumn: string;
  valueColumn: string;
  buttonColumn: string;
  body: string;
  emptyState: string;
  addButton: string;
}

export interface IKeyValuePairEditorStyles {
  root?: IStyle;
  header: IStyle;
  keyColumn: IStyle;
  valueColumn: IStyle;
  buttonColumn: IStyle;
  body: IStyle;
  emptyState: IStyle;
  addButton: IStyle;
}

export interface IKeyValuePairClassnames {
  row: string;
  keyColumn: string;
  valueColumn: string;
  buttonColumn: string;
  input: string;
  textArea: string;
  validationInputError: string;
  validationErrorMessage: string;
}

export interface IKeyValuePairStyles {
  row: IStyle;
  keyColumn: IStyle;
  valueColumn: IStyle;
  buttonColumn: IStyle;
  input: IStyle;
  textArea: IStyle;
  validationInputError: IStyle;
  validationErrorMessage: IStyle;
}

export const keyValuePairStyles = (theme: IH2OTheme): IKeyValuePairStyles => {
  return {
    row: {
      display: 'flex',
      padding: '10px 0',
      borderTop: `solid 1px ${theme.semanticColors?.bodyBackground}`,
      ':first-child': {
        borderTop: 'none',
      },
      '.more, .cancel': {
        width: '30px',
      },
      fontFamily: 'monospace',
    },
    keyColumn: {
      width: 'calc(50% - 35px)',
      flex: '0 0 auto',
      marginRight: '10px',
      overflow: 'auto',
    },
    valueColumn: {
      width: 'calc(50% - 35px)',
      flex: '0 0 auto',
      pre: {
        padding: 0,
        margin: 0,
        overflow: 'auto',
      },
    },
    buttonColumn: {
      width: '70px',
      flex: '0 0 auto',
      display: 'flex',
      justifyContent: 'flex-end',
      '> div': {
        marginLeft: '5px',
        button: {
          width: '30px',
        },
      },
    },
    input: {
      width: '100%',
      height: Sizes.height,
      border: `1px solid ${theme.semanticColors?.inputBorder}`,
      borderRadius: Sizes.borderRadius,
      ':focus, :active': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      color: theme.semanticColors?.inputText,
      paddingLeft: '7px',
      paddingRight: '7px',
      lineHeight: '1.5em',
      '::placeholder': {
        color: theme.semanticColors?.inputPlaceholder,
      },
      fontFamily: 'inherit',
    },
    textArea: {
      resize: 'vertical',
      display: 'block',
      whiteSpace: 'nowrap',
      width: '100%',
      overflow: 'auto',
      height: Sizes.height,
      border: `1px solid ${theme.semanticColors?.inputBorder}`,
      borderRadius: Sizes.borderRadius,
      ':focus, :active': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      color: theme.semanticColors?.inputText,
      padding: '5px 7px',
      lineHeight: '1.5em',
      '::placeholder': {
        color: theme.semanticColors?.inputPlaceholder,
      },
      fontFamily: 'inherit',
    },
    validationErrorMessage: {
      fontFamily: theme.defaultFontStyle?.fontFamily,
      color: theme.semanticColors?.inputErrorMessageText,
      fontWeight: 600,
      padding: '5px',
    },
    validationInputError: {
      borderColor: `${theme.semanticColors?.inputErrorBorder} !important`,
    },
  };
};

export const moreButtonStyles = (): IButtonStyles => ({
  root: {
    height: 'auto',
    lineHeight: 'auto',
  },
  menuIcon: {
    height: 'auto',
    lineHeight: 'auto',
  },
});

export const keyValuePairEditorStyles = (theme: IH2OTheme): IKeyValuePairEditorStyles => {
  return {
    root: {},
    header: {
      display: 'flex',
      backgroundColor: theme.semanticColors?.bodyBackground,
      borderRadius: `${Sizes.borderRadius}px ${Sizes.borderRadius}px 0 0`,
      fontWeight: FontWeights.semiBold,
      position: 'relative',
    },
    keyColumn: {
      padding: '10px',
      width: 'calc(50% - 35px)',
      flex: '0 0 auto',
    },
    valueColumn: {
      padding: '10px 10px 10px 0',
      width: 'calc(50% - 35px)',
      flex: '0 0 auto',
    },
    buttonColumn: {
      padding: '10px',
      flex: '0 0 auto',
      display: 'flex',
      width: '70px',
      justifyContent: 'flex-end',
    },
    emptyState: {},
    body: {
      padding: '0 10px',
      border: `solid 1px ${theme.semanticColors?.bodyBackground}`,
      borderRadius: `0 0 ${Sizes.borderRadius} ${Sizes.borderRadius}`,
    },
    addButton: {
      height: '25px',
      width: '25px',
      lineHeight: '23px',
      textAlign: 'center',
      borderRadius: '3px',
      border: `solid 1px ${theme.semanticColors?.inputBorder}`,
      outline: 'none',
      position: 'absolute',
      top: '6px',
      right: '8px',
      fontWeight: FontWeights.fw200,
      fontSize: '25px',
      padding: '0',
      fontFamily: theme.defaultFontStyle?.fontFamily,
      backgroundColor: theme.semanticColors?.contentBackground,
      cursor: 'pointer',
      ':focus, :active': {
        borderColor: theme.semanticColors?.inputActiveBorder,
      },
      ':disabled': {
        cursor: 'default',
        borderColor: theme.semanticColors?.inputBorder,
      },
      '> span': {
        fontFamily: theme.defaultFontStyle?.fontFamily,
        position: 'absolute',
        top: '-1px',
        left: '3px',
      },
    },
  };
};
